// src/components/AddPaymentModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from '../api/axios';
import { handleAddItem } from '../utils/crmActions';
import { toast } from 'react-toastify';
import {
  CreditCard,
  CalendarDays,
  FileText,
  Info,
  User as UserIcon,
  Search as SearchIcon,
} from 'lucide-react';

/* -------------------------------- UI helpers ------------------------------- */
const INPUT_BASE =
  'block w-full rounded-lg border border-gray-300 bg-white px-3.5 py-2.5 text-[0.95rem] leading-6 text-gray-900 placeholder:text-gray-400 shadow-sm focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500';
const SELECT_BASE = INPUT_BASE + ' pr-9';
const LABEL_BASE = 'block text-[13px] font-medium text-gray-700';
const HINT_BASE = 'text-[12px] text-gray-500';

function LabeledInput({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  required = false,
  autoComplete,
  inputRef,
  invalid = false,
  min,
  step,
  disabled = false,
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <input
        ref={inputRef}
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={type}
        autoComplete={autoComplete}
        className={`${INPUT_BASE} ${
          invalid ? 'border-red-500 focus:border-red-500 focus:ring-red-100' : ''
        } ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
        required={required}
        aria-invalid={invalid || undefined}
        min={min}
        step={step}
        disabled={disabled}
      />
    </div>
  );
}

function LabeledSelect({
  id,
  name,
  label,
  value,
  onChange,
  children,
  required = false,
  disabled = false,
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label} {required ? <span className="text-red-500">*</span> : null}
      </label>
      <select
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        className={`${SELECT_BASE} ${disabled ? 'bg-gray-50 text-gray-500 cursor-not-allowed' : ''}`}
        disabled={disabled}
        required={required}
      >
        {children}
      </select>
    </div>
  );
}

function LabeledTextArea({
  id,
  name,
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id || name} className={LABEL_BASE}>
        {label}
      </label>
      <textarea
        id={id || name}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        className={`${INPUT_BASE} align-top resize-y`}
      />
    </div>
  );
}

function Section({ icon: Icon, title, hint, children }) {
  return (
    <div className="border rounded-xl p-5 space-y-4 shadow-sm bg-white">
      <div className="flex items-start gap-2">
        <div className="mt-0.5 text-gray-700">
          <Icon size={18} />
        </div>
        <div>
          <div className="text-gray-800 font-semibold">{title}</div>
          {hint ? <div className={HINT_BASE}>{hint}</div> : null}
        </div>
      </div>
      {children}
    </div>
  );
}

/* ------------------------------ constants ---------------------------------- */

const METHOD_OPTIONS = [
  { value: '', label: '— Select method —' },
  { value: 'card', label: 'Card' },
  { value: 'cash', label: 'Cash' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'e_transfer', label: 'E-transfer' },
  { value: 'other', label: 'Other' },
];

const fmtMoney = (n, c = 'CAD') => {
  const num = typeof n === 'number' ? n : Number(n || 0);
  return num.toLocaleString(undefined, {
    style: 'currency',
    currency: c || 'CAD',
  });
};

const fmtDateOnlyUTC = (d) =>
  d
    ? new Date(d).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'UTC',
      })
    : '—';

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  if (Array.isArray(d?.data)) return d.data;
  if (Array.isArray(d?.items)) return d.items;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res)) return res;
  return [];
};

/* ------------------------------ component ---------------------------------- */

/**
 * AddPaymentModal
 *
 * Multi-context:
 * - Global: no invoiceId / clientId → user picks both.
 * - From Invoice: invoiceId provided → client+invoice locked & prefilled.
 * - From Client: clientId provided (no invoiceId) → client locked, invoice picker filtered.
 *
 * Props:
 * - invoiceId        (optional; if present → "invoice" context)
 * - clientId         (optional; if present → "client" context)
 * - invoiceNumber    (optional; header only)
 * - clientName       (optional; header only)
 * - currency         (optional; default from invoice or CAD)
 * - defaultAmount    (optional; fallback to invoice.balanceDue / totalAmount)
 * - mode             (optional; force 'invoice' | 'client' | 'global')
 * - onClose()
 * - onSuccess(createdPayment)
 */
const AddPaymentModal = ({
  invoiceId: invoiceIdProp,
  clientId: clientIdProp,
  invoiceNumber: invoiceNumberProp,
  clientName: clientNameProp,
  currency: currencyProp,
  defaultAmount,
  mode,
  onClose,
  onSuccess,
}) => {
  // Infer context from props unless explicitly forced
  const inferredContext = useMemo(() => {
    if (mode) return mode;
    if (invoiceIdProp) return 'invoice';
    if (clientIdProp) return 'client';
    return 'global';
  }, [mode, invoiceIdProp, clientIdProp]);

  const isInvoiceContext = inferredContext === 'invoice';
  const isClientContext = inferredContext === 'client';
  const isGlobalContext = inferredContext === 'global';

  // Linked entities (state so user can change in global/client mode)
  const [linkedClientId, setLinkedClientId] = useState(clientIdProp || null);
  const [linkedInvoiceId, setLinkedInvoiceId] = useState(invoiceIdProp || null);

  // Invoice details + summary
  const [invoice, setInvoice] = useState(null);
  const [loadingInvoice, setLoadingInvoice] = useState(!!invoiceIdProp);
  const [invoiceError, setInvoiceError] = useState('');

  // Client + invoice lists for pickers
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsError, setClientsError] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [loadingInvoiceOptions, setLoadingInvoiceOptions] = useState(false);
  const [invoiceOptionsError, setInvoiceOptionsError] = useState('');
  const [invoiceSearch, setInvoiceSearch] = useState('');

  // Form
  const [form, setForm] = useState(() => {
    const today = new Date().toISOString().slice(0, 10);
    return {
      amount: '',
      paymentDate: today,
      method: '',
      reference: '',
      notes: '',
    };
  });
  const [amountError, setAmountError] = useState('');
  const amountRef = useRef(null);

  /* ------------------------------ fetch invoice ----------------------------- */

  // Fetch invoice details whenever the linked invoice id changes
  useEffect(() => {
    const invoiceId = linkedInvoiceId;
    if (!invoiceId) {
      setInvoice(null);
      setLoadingInvoice(false);
      setInvoiceError('');
      return;
    }

    let mounted = true;
    const ctrl = new AbortController();

    setLoadingInvoice(true);
    setInvoiceError('');

    axios
      .get(`/invoices/${invoiceId}`, { signal: ctrl.signal })
      .then((res) => {
        if (!mounted) return;
        const data = res?.data?.data || res?.data || null;
        setInvoice(data || null);

        // If client not yet bound (global), derive from invoice
        const invoiceClientId =
          data?.clientId || data?.client?.id || data?.client?._id || null;
        if (!linkedClientId && invoiceClientId) {
          setLinkedClientId(invoiceClientId);
        }

        const balance = data?.balanceDue ?? data?.balance ?? data?.totalAmount ?? null;
        if (!form.amount && (defaultAmount != null || balance != null)) {
          setForm((prev) => ({
            ...prev,
            amount:
              defaultAmount != null
                ? String(defaultAmount)
                : balance != null
                ? String(balance)
                : '',
          }));
        }
      })
      .catch((err) => {
        if (!mounted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setInvoice(null);
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Unknown error';
        setInvoiceError(msg);
        toast.error('Failed to load invoice details. You can still record a payment manually.');
      })
      .finally(() => mounted && setLoadingInvoice(false));

    return () => {
      mounted = false;
      ctrl.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedInvoiceId, defaultAmount]);

  /* ------------------------------ fetch clients ----------------------------- */

  // Only need client list when user can choose client (global or client context with unlocked client)
  const clientSelectionEnabled = isGlobalContext;

  useEffect(() => {
    if (!clientSelectionEnabled) return;

    let mounted = true;
    const ctrl = new AbortController();

    setLoadingClients(true);
    setClientsError('');

    axios
      .get('/clients', {
        signal: ctrl.signal,
        params: {
          page: 1,
          limit: 50,
          search: clientSearch || undefined,
          sortBy: 'name',
          sortOrder: 'asc',
        },
      })
      .then((res) => {
        if (!mounted) return;
        const arr = toArray(res);
        setClients(Array.isArray(arr) ? arr : []);
      })
      .catch((err) => {
        if (!mounted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setClients([]);
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load clients';
        setClientsError(msg);
      })
      .finally(() => mounted && setLoadingClients(false));

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [clientSelectionEnabled, clientSearch]);

  /* ------------------------------ fetch invoices for picker ----------------- */

  // We fetch invoice list only when:
  // - There is a client selected, and
  // - We are NOT in "invoice" context (where invoice is locked)
  const invoiceSelectionEnabled = !isInvoiceContext && !!linkedClientId;

  useEffect(() => {
    if (!invoiceSelectionEnabled) {
      setInvoiceOptions([]);
      setInvoiceOptionsError('');
      return;
    }

    let mounted = true;
    const ctrl = new AbortController();

    setLoadingInvoiceOptions(true);
    setInvoiceOptionsError('');

    axios
      .get('/invoices', {
        signal: ctrl.signal,
        params: {
          page: 1,
          limit: 50,
          clientId: linkedClientId,
          search: invoiceSearch || undefined,
          sortBy: 'issueDate',
          sortOrder: 'desc',
        },
      })
      .then((res) => {
        if (!mounted) return;
        const arr = toArray(res);
        setInvoiceOptions(Array.isArray(arr) ? arr : []);
      })
      .catch((err) => {
        if (!mounted || err?.name === 'CanceledError' || err?.name === 'AbortError') return;
        setInvoiceOptions([]);
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Failed to load invoices';
        setInvoiceOptionsError(msg);
      })
      .finally(() => mounted && setLoadingInvoiceOptions(false));

    return () => {
      mounted = false;
      ctrl.abort();
    };
  }, [invoiceSelectionEnabled, linkedClientId, invoiceSearch]);

  /* ------------------------------ change handlers --------------------------- */

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === 'amount') {
      setAmountError('');
      if (amountRef.current) {
        amountRef.current.setCustomValidity('');
      }
    }
  };

  const handleClientChange = (e) => {
    const val = e.target.value || null;
    setLinkedClientId(val);
    // Reset invoice selection when client changes (global)
    if (!isInvoiceContext) {
      setLinkedInvoiceId(null);
      setInvoice(null);
      setInvoiceSearch('');
      // amount will be re-derived when invoice is chosen
      setForm((prev) => ({ ...prev, amount: '' }));
    }
  };

  const handleInvoiceChange = (e) => {
    const val = e.target.value || null;
    setLinkedInvoiceId(val);
    // amount will be set when invoice details load
    setForm((prev) => ({ ...prev, amount: '' }));
  };

  /* ------------------------------ derived data ------------------------------ */

  const effectiveCurrency =
    currencyProp || invoice?.currency || invoice?.billingCurrency || 'CAD';

  const effectiveInvoiceNumber =
    invoiceNumberProp || invoice?.invoiceNumber || '';

  const effectiveClientName =
    clientNameProp ||
    invoice?.clientSnapshot?.name ||
    invoice?.clientName ||
    invoice?.client?.name ||
    '';

  const effectiveClientId = useMemo(
    () =>
      linkedClientId ||
      invoice?.clientId ||
      invoice?.client?.id ||
      invoice?.client?._id ||
      null,
    [linkedClientId, invoice]
  );

  const remainingBalance = useMemo(() => {
    if (invoice) {
      if (invoice.balanceDue != null) return invoice.balanceDue;
      if (invoice.balance != null) return invoice.balance;
      if (invoice.totalAmount != null) return invoice.totalAmount;
    }
    return defaultAmount ?? null;
  }, [invoice, defaultAmount]);

  /* ------------------------------ submit ------------------------------------ */

  const handleSubmit = async (e) => {
    e.preventDefault();

    const invoiceId = linkedInvoiceId;
    const clientId = effectiveClientId;

    if (!clientId) {
      toast.error('Please select a client for this payment.');
      return;
    }

    if (!invoiceId) {
      toast.error('Please select an invoice for this payment.');
      return;
    }

    const rawAmount = form.amount != null ? String(form.amount).trim() : '';
    const numericAmount = Number(rawAmount);

    if (!rawAmount || !Number.isFinite(numericAmount) || numericAmount <= 0) {
      const msg = 'Please enter a valid positive amount.';
      setAmountError(msg);
      if (amountRef.current) {
        amountRef.current.setCustomValidity(msg);
        amountRef.current.reportValidity();
      }
      return;
    }

    // Soft guard if amount > remaining balance
    if (remainingBalance != null && numericAmount > Number(remainingBalance) + 0.0001) {
      const proceed = window.confirm(
        `This payment (${fmtMoney(
          numericAmount,
          effectiveCurrency
        )}) is greater than the remaining balance (${fmtMoney(
          remainingBalance,
          effectiveCurrency
        )}). Continue?`
      );
      if (!proceed) return;
    }

    const payload = {
      invoiceId,
      clientId,
      amount: numericAmount,
      currency: effectiveCurrency,
      paymentDate: form.paymentDate || undefined,
      method: form.method || null,
      reference: form.reference || undefined,
      notes: form.notes || undefined,
      source: 'manual', // manual entry from UI
      status: 'succeeded', // we record successful payments only
    };

    await handleAddItem('payments', payload, onSuccess, onClose);
  };

  const formId = 'add-payment-form';

  /* ------------------------------ helpers (display) ------------------------- */

  const clientOptions = useMemo(
    () =>
      (clients || []).map((c) => {
        const id = c.id || c._id;
        const label =
          c.name || c.displayName || c.company || c.email || 'Unnamed client';
        return { id, label };
      }),
    [clients]
  );

  const invoiceSelectOptions = useMemo(
    () =>
      (invoiceOptions || []).map((inv) => {
        const id = inv.id || inv._id;
        const num = inv.invoiceNumber || '—';
        const total = inv.totalAmount != null ? fmtMoney(inv.totalAmount, inv.currency || effectiveCurrency) : '';
        const due = inv.dueDate ? fmtDateOnlyUTC(inv.dueDate) : '';
        const status = inv.status ? String(inv.status).charAt(0).toUpperCase() + String(inv.status).slice(1) : '';
        const labelParts = [num, total, status && `• ${status}`, due && `• Due ${due}`].filter(Boolean);
        return { id, label: labelParts.join(' ') };
      }),
    [invoiceOptions, effectiveCurrency]
  );

  const showClientPicker = clientSelectionEnabled;
  const showInvoicePicker = !isInvoiceContext;

  const headerSuffix =
    effectiveInvoiceNumber
      ? ` · ${effectiveInvoiceNumber}`
      : isClientContext && effectiveClientName
      ? ` · ${effectiveClientName}`
      : '';

  /* ------------------------------ render ------------------------------------ */

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="relative bg-white w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b bg-gradient-to-r from-emerald-600 to-emerald-500 text-white rounded-t-2xl">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center shrink-0">
              <CreditCard size={28} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xl font-semibold truncate">
                Record Payment{headerSuffix}
              </h2>
              <p className="text-sm text-emerald-100">
                Link this payment to a client and invoice so balances stay accurate.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="bg-white/20 hover:bg-white/30 rounded-full p-2"
            type="button"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Form body */}
        <form
          id={formId}
          onSubmit={handleSubmit}
          className="flex-1 overflow-y-auto p-8 space-y-8 bg-gray-50/50"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main fields */}
            <div className="lg:col-span-2 space-y-6">
              {/* Payment details */}
              <Section
                icon={CreditCard}
                title="Payment Details"
                hint="Core payment information applied to the selected invoice"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <LabeledInput
                    name="amount"
                    label={`Amount (${effectiveCurrency})`}
                    value={form.amount}
                    onChange={handleChange}
                    placeholder="e.g., 250.00"
                    type="number"
                    required
                    inputRef={amountRef}
                    invalid={!!amountError}
                    min="0"
                    step="0.01"
                  />
                  <LabeledInput
                    name="paymentDate"
                    label="Payment Date"
                    value={form.paymentDate}
                    onChange={handleChange}
                    type="date"
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <LabeledSelect
                    name="method"
                    label="Payment Method"
                    value={form.method}
                    onChange={handleChange}
                    required={false}
                  >
                    {METHOD_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </LabeledSelect>

                  <LabeledInput
                    name="reference"
                    label="Reference"
                    value={form.reference}
                    onChange={handleChange}
                    placeholder="Cheque #, e-transfer ref, last 4, etc."
                  />
                </div>

                <div className="mt-4">
                  <LabeledTextArea
                    name="notes"
                    label="Internal Notes"
                    value={form.notes}
                    onChange={handleChange}
                    placeholder="Optional notes visible only inside Asteriq (not on the invoice)"
                    rows={3}
                  />
                </div>

                {amountError ? (
                  <div className="mt-2 text-sm text-red-600" aria-live="polite">
                    {amountError}
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-gray-500">
                    The invoice balance will be updated automatically when this payment is saved.
                  </div>
                )}
              </Section>

              {/* Info */}
              <Section
                icon={Info}
                title="Reconciliation Tips"
                hint="Use references and notes to keep Stripe / bank records in sync"
              >
                <ul className="text-[13px] text-gray-600 space-y-1 list-disc pl-4">
                  <li>Match the amount and date to your bank or Stripe records.</li>
                  <li>
                    Use the reference field for cheque numbers, e-transfer IDs, or Stripe
                    transaction IDs.
                  </li>
                  <li>
                    If a payment fails or is refunded, manage that from the Payments list or Stripe
                    directly.
                  </li>
                </ul>
              </Section>
            </div>

            {/* Sidebar: link + summary */}
            <div className="space-y-6">
              {/* Client + Invoice selector */}
              <Section
                icon={UserIcon}
                title="Client & Invoice"
                hint={
                  isInvoiceContext
                    ? 'This payment is locked to the selected invoice.'
                    : 'Select a client and invoice to apply this payment.'
                }
              >
                {/* Client picker / display */}
                {isInvoiceContext || isClientContext ? (
                  <div className="space-y-1.5">
                    <div className={LABEL_BASE}>Client</div>
                    <div className="text-sm font-medium text-gray-900">
                      {effectiveClientName || 'Unknown client'}
                    </div>
                    {isInvoiceContext && (
                      <div className="text-xs text-gray-500">
                        Linked via invoice; cannot be changed here.
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-2 mb-2">
                      <SearchIcon size={14} className="text-gray-400" />
                      <input
                        type="text"
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        placeholder="Search clients by name, email..."
                        className="flex-1 text-[13px] px-2 py-1.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100"
                      />
                    </div>
                    <LabeledSelect
                      name="clientId"
                      label="Client"
                      value={linkedClientId || ''}
                      onChange={handleClientChange}
                      required
                      disabled={!showClientPicker}
                    >
                      <option value="">— Select client —</option>
                      {loadingClients && <option disabled>Loading clients…</option>}
                      {clientOptions.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.label}
                        </option>
                      ))}
                    </LabeledSelect>
                    {clientsError && (
                      <div className="mt-1 text-xs text-red-600" aria-live="polite">
                        {clientsError}
                      </div>
                    )}
                  </>
                )}

                {/* Invoice picker / display */}
                <div className="mt-4">
                  {isInvoiceContext ? (
                    <div className="space-y-1.5">
                      <div className={LABEL_BASE}>Invoice</div>
                      <div className="text-sm font-medium text-gray-900">
                        {effectiveInvoiceNumber || 'Unknown invoice'}
                      </div>
                      <div className="text-xs text-gray-500">
                        This payment is linked to this invoice.
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-2 mb-2">
                        <SearchIcon size={14} className="text-gray-400" />
                        <input
                          type="text"
                          value={invoiceSearch}
                          onChange={(e) => setInvoiceSearch(e.target.value)}
                          placeholder={
                            linkedClientId
                              ? 'Search invoices by number…'
                              : 'Select a client first'
                          }
                          disabled={!linkedClientId}
                          className={`flex-1 text-[13px] px-2 py-1.5 border rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 ${
                            !linkedClientId ? 'bg-gray-50 text-gray-400 cursor-not-allowed' : ''
                          }`}
                        />
                      </div>
                      <LabeledSelect
                        name="invoiceId"
                        label="Invoice"
                        value={linkedInvoiceId || ''}
                        onChange={handleInvoiceChange}
                        required
                        disabled={!linkedClientId}
                      >
                        <option value="">
                          {linkedClientId ? '— Select invoice —' : 'Select client first'}
                        </option>
                        {linkedClientId && loadingInvoiceOptions && (
                          <option disabled>Loading invoices…</option>
                        )}
                        {linkedClientId &&
                          invoiceSelectOptions.map((inv) => (
                            <option key={inv.id} value={inv.id}>
                              {inv.label}
                            </option>
                          ))}
                      </LabeledSelect>
                      {invoiceOptionsError && (
                        <div className="mt-1 text-xs text-red-600" aria-live="polite">
                          {invoiceOptionsError}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Section>

              {/* Invoice Summary */}
              <Section
                icon={FileText}
                title="Invoice Summary"
                hint="Context for the payment you're recording"
              >
                {loadingInvoice && linkedInvoiceId ? (
                  <div className="space-y-2 text-sm text-gray-500">
                    <div className="h-4 w-2/3 rounded bg-gray-200 animate-pulse" />
                    <div className="h-4 w-1/2 rounded bg-gray-200 animate-pulse" />
                    <div className="h-4 w-1/3 rounded bg-gray-200 animate-pulse" />
                  </div>
                ) : invoice ? (
                  <div className="space-y-2 text-sm">
                    {effectiveInvoiceNumber && (
                      <div className="flex justify-between">
                        <span className="text-gray-500">Invoice #</span>
                        <span className="font-medium text-gray-900">
                          {effectiveInvoiceNumber}
                        </span>
                      </div>
                    )}
                    {effectiveClientName && (
                      <div className="flex justify-between items-center gap-3">
                        <span className="text-gray-500 flex items-center gap-1">
                          <UserIcon size={14} /> Client
                        </span>
                        <span className="font-medium text-gray-900 text-right">
                          {effectiveClientName}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-500">Issue Date</span>
                      <span className="text-gray-900">
                        {fmtDateOnlyUTC(invoice.issueDate)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Due Date</span>
                      <span className="text-gray-900">{fmtDateOnlyUTC(invoice.dueDate)}</span>
                    </div>
                    <div className="border-t my-2" />
                    <div className="flex justify-between">
                      <span className="text-gray-500">Total</span>
                      <span className="font-semibold text-gray-900">
                        {fmtMoney(invoice.totalAmount, effectiveCurrency)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Remaining</span>
                      <span className="font-semibold text-emerald-700">
                        {fmtMoney(remainingBalance ?? 0, effectiveCurrency)}
                      </span>
                    </div>
                  </div>
                ) : linkedInvoiceId ? (
                  <div className="text-sm text-red-600">
                    {invoiceError || 'Invoice details unavailable.'}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Select an invoice to see its summary and remaining balance.
                  </div>
                )}
              </Section>

              <Section
                icon={CalendarDays}
                title="Applied Effects"
                hint="What happens when this payment is saved"
              >
                <ul className="text-[13px] text-gray-600 space-y-1 list-disc pl-4">
                  <li>The invoice balance and payment history will be updated.</li>
                  <li>
                    If the invoice becomes fully paid, its status will move to{' '}
                    <span className="font-medium">Paid</span>.
                  </li>
                  <li>
                    If the payment is partial, the status will be{' '}
                    <span className="font-medium">Partial</span>.
                  </li>
                </ul>
              </Section>
            </div>
          </div>
        </form>

        {/* Sticky footer */}
        <div className="px-8 py-4 bg-white border-t flex justify-end gap-3 rounded-b-2xl shadow-lg">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-gray-700 font-medium hover:bg-gray-100 border border-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            form={formId}
            className="px-7 py-2 bg-emerald-600 text-white rounded-lg font-semibold hover:bg-emerald-700 shadow-sm"
          >
            Record Payment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddPaymentModal;
